import { useEffect, useMemo, useState } from 'react';
import { X, Delete, ShieldAlert, Check, Plus, Trash2 } from '../../lib/icons';
import { toast } from 'sonner';
import {
  fetchCuves,
  fetchFormats,
  declarerSortieHuile,
  familleLot,
  nomTiers,
  decompte,
  tauxRetenue,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';
import { estBlocageFiliere } from '../../lib/dataProvider';
import { passageHomogene } from '../../lib/minimumTrituration';

const TOUCHES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '00', 'del'];

export const SortieHuileDialog = ({ lots, onClose, onDone }) => {
  const [poids, setPoids] = useState('');
  const [lignes, setLignes] = useState([{ cuveId: '', kg: '' }]);
  const [cuves, setCuves] = useState([]);
  const [formats, setFormats] = useState([]);
  const [emb, setEmb] = useState({});
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [iGroupe, setIGroupe] = useState(0);
  // Un total d'huile MESURÉ ne se répartit jamais entre deux clients : les lots sont
  // groupés par tiers et déclarés un passage à la fois (OF multiclients autorisés).
  const groupes = useMemo(() => {
    const m = new Map();
    lots.forEach((l) => {
      const cle = l.parcours === 'client' ? `client:${l.tiersId ?? l.id}` : 'fournisseurs';
      if (!m.has(cle))
        m.set(cle, {
          cle,
          libelle: l.parcours === 'client' ? nomTiers(l.tiersId) : 'Achats d’olives (fournisseurs)',
          lots: [],
        });
      m.get(cle).lots.push(l);
    });
    return [...m.values()];
  }, [lots]);
  const groupe = groupes[Math.min(iGroupe, Math.max(0, groupes.length - 1))];
  const lotsActifs = groupe?.lots ?? [];
  // Devenir à décider à la trituration (bon éclair sans destination fixée au pont-bascule).
  const devenirOuvert = lotsActifs.some((l) => l.parcours === 'client') && lotsActifs.every((l) => l.parcours === 'client' && !l.devenir);
  const [choixDevenir, setChoixDevenir] = useState('cuve');

  useEffect(() => {
    fetchCuves().then(setCuves);
    fetchFormats().then(setFormats);
  }, []);

  const sortieDirecte = devenirOuvert ? choixDevenir === 'sortie' : lotsActifs.every((l) => l.parcours === 'client' && l.devenir === 'sortie');
  const clientLot = lotsActifs.some((l) => l.parcours === 'client');
  // Minimum de facturation : un passage ne se calcule jamais avec des tarifs ou des
  // décisions divergents — harmonisation par un utilisateur autorisé avant déclaration.
  const lotsClientActifs = lotsActifs.filter((l) => l.parcours === 'client');
  const harmonisation = lotsClientActifs.length > 1 ? passageHomogene(lotsClientActifs) : { homogene: true, tarifs: [] };
  const lignesEmb = Object.entries(emb)
    .filter(([, q]) => Number(q) > 0)
    .map(([formatId, qte]) => ({ formatId, qte: Number(qte) }));
  const stepEmb = (id, d) => setEmb((v) => ({ ...v, [id]: Math.max(0, (Number(v[id]) || 0) + d) }));

  const famille = familleLot(lotsActifs[0] ?? lots[0]);
  const { user } = useAuth();
  const poidsNetTotal = lotsActifs.reduce((s, r) => s + decompte(r).poidsNet, 0);
  const netRetenu = sortieDirecte ? 0 : lotsActifs.filter((l) => l.parcours === 'client').reduce((s, r) => s + decompte(r).poidsNet, 0);
  const q = Number(poids) || 0;
  const TAUX = tauxRetenue();
  const retenue = Number((q * (poidsNetTotal > 0 ? netRetenu / poidsNetTotal : 0) * TAUX).toFixed(3));
  // La retenue reste physiquement en cuve (stock huilerie) : tout le volume est réparti.
  const aVerser = q;
  const reparti = Number(lignes.reduce((s, l) => s + (Number(l.kg) || 0), 0).toFixed(3));
  const reste = Number((aVerser - reparti).toFixed(3));
  const rendement = poidsNetTotal > 0 ? (aVerser / poidsNetTotal) * 100 : 0;
  const croisee = lignes
    .map((l) => cuves.find((c) => c.id === l.cuveId))
    .find((c) => c && c.famille !== famille);

  const majLigne = (i, k, v) => {
    setErreur('');
    setLignes((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  };
  const ajouterLigne = () => setLignes((l) => [...l, { cuveId: '', kg: '' }]);
  const retirerLigne = (i) => setLignes((l) => (l.length === 1 ? l : l.filter((_, j) => j !== i)));

  const taper = (t) => {
    setErreur('');
    if (t === 'del') return setPoids((p) => p.slice(0, -1));
    setPoids((p) => (p + t).replace(/^0+(?=\d)/, '').slice(0, 7));
  };

  const valider = async (override = false) => {
    setEnCours(true);
    const { error, retenue: r, cuves: dest, sortieDirecte: directe, emballagesMontant } = await declarerSortieHuile({
      ids: lotsActifs.map((l) => l.id),
      poidsHuile: q,
      repartition: lignes,
      emballages: lignesEmb,
      directe: devenirOuvert ? sortieDirecte : undefined,
      override,
      utilisateur: user?.name,
    });
    setEnCours(false);
    if (error) {
      if (!override && estBlocageFiliere(error) && estAdmin(user?.role) && window.confirm(`${error}\n\nOVERRIDE ADMINISTRATEUR : forcer quand même ? L'opération sera tracée dans le journal d'audit.`)) return valider(true);
      setErreur(error);
      toast.error(error);
      return;
    }
    toast.success(
      directe
        ? `${groupe.libelle} — ${fmt(aVerser, 1)} Kg remis directement au client${emballagesMontant > 0 ? ' — emballages à régler en caisse' : ''}`
        : `${groupe.libelle} — ${fmt(aVerser, 1)} Kg répartis sur ${dest.length} cuve(s)${r > 0 ? ` — retenue de ${fmt(r, 3)} Kg conservée en nature (aucune valeur financière)` : ''}`
    );
    onDone();
    // Passages restants : on enchaîne sur le groupe suivant avec son propre total mesuré.
    if (groupes.length > 1) {
      setPoids('');
      setLignes([{ cuveId: '', kg: '' }]);
      setEmb({});
      setErreur('');
      setIGroupe(0);
      return;
    }
    onClose();
  };

  const cuvesTriees = useMemo(
    () => [...cuves].sort((a, b) => (a.famille === famille ? -1 : 1) - (b.famille === famille ? -1 : 1)),
    [cuves, famille]
  );
  const bloque =
    enCours ||
    !harmonisation.homogene ||
    (!sortieDirecte && ((Boolean(croisee) && !estAdmin(user?.role)) || reparti <= 0 || Math.abs(reste) > 0.05)) ||
    !q;

  if (!groupe) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="sortie-huile-dialog">
      <div className="max-h-[95vh] w-full max-w-3xl overflow-y-auto border-2 border-slate-900 bg-white">
        <div className="flex items-start justify-between border-b-2 border-slate-900 px-5 py-4">
          <div>
            <h2 className="font-heading text-xl font-black tracking-tight text-slate-900">
              Déclaration de fin de lot
            </h2>
            <p className="font-data text-xs uppercase tracking-[0.1em] text-slate-600">
              {lotsActifs.length} lot{lotsActifs.length > 1 ? 's' : ''} — {fmt(poidsNetTotal)} Kg olives —{' '}
              {famille === 'lampante' ? 'FILIÈRE LAMPANTE' : 'FILIÈRE NOBLES'}
            </p>
          </div>
          <button
            type="button"
            data-testid="sortie-close"
            onClick={onClose}
            className="border-2 border-slate-300 p-3 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {!harmonisation.homogene && (
          <p data-testid="sortie-harmonisation" className="border-b-2 border-red-600 bg-red-50 px-5 py-3 text-xs font-bold text-red-800">
            HARMONISATION REQUISE — les bons de ce passage portent des tarifs de trituration ou des
            décisions de dérogation différents ({harmonisation.tarifs.map((t) => `${t} TND/kg`).join(' · ')}).
            Le minimum de facturation ne sera pas calculé en silence : faites harmoniser ces bons par
            un utilisateur autorisé, ou déclarez-les séparément.
          </p>
        )}

        {groupes.length > 1 && (
          <div data-testid="sortie-groupes" className="border-b-2 border-sky-600 bg-sky-50 px-5 py-3">
            <p data-testid="sortie-groupe-actif" className="font-data text-xs font-bold uppercase tracking-[0.08em] text-sky-900">
              Passage {Math.min(iGroupe, groupes.length - 1) + 1} / {groupes.length} — {groupe.libelle}
            </p>
            <p className="mt-0.5 font-data text-[11px] text-sky-800">
              Un total mesuré par passage : l'huile n'est jamais répartie entre des clients
              différents. Enregistrez ce passage, puis saisissez le suivant.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {groupes.map((g, i) => (
                <button
                  key={g.cle}
                  type="button"
                  data-testid={`sortie-groupe-${i}`}
                  onClick={() => {
                    setIGroupe(i);
                    setPoids('');
                    setLignes([{ cuveId: '', kg: '' }]);
                    setErreur('');
                  }}
                  className={`h-9 border px-3 font-data text-xs font-bold transition-colors duration-150 ${
                    i === Math.min(iGroupe, groupes.length - 1)
                      ? 'border-sky-700 bg-sky-700 text-white'
                      : 'border-sky-300 bg-white text-sky-800 hover:bg-sky-100'
                  }`}
                >
                  {g.libelle} ({g.lots.length})
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-2">
          <div className="space-y-4 bg-white px-5 py-5">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Poids d'huile extraite (Kg)
              </p>
              <p
                data-testid="sortie-poids"
                className="tabular mt-2 flex h-20 items-center justify-end border-2 border-slate-900 bg-slate-50 px-4 font-data text-4xl font-semibold text-slate-900"
              >
                {poids || '0'}
              </p>
              <p className="mt-2 font-data text-xs text-slate-500">
                Rendement net client : {fmt(rendement, 2)} %
              </p>
              <div data-testid="sortie-retenue" className="mt-3 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
                <div className="bg-amber-50 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.08em] text-amber-800">
                    Retenue huilerie ({(TAUX * 100).toFixed(2).replace('.', ',')} %)
                  </p>
                  <p data-testid="retenue-kg" className="tabular font-data text-lg font-semibold text-amber-900">{fmt(retenue, 3)} Kg</p>
                  <p className="text-[10px] text-amber-800">
                    {netRetenu > 0
                      ? `Sur ${fmt(netRetenu)} Kg d’olives de clients stockant à l’huilerie → reste en cuve, bascule au stock huilerie en nature (valeur financière nulle)`
                      : 'Aucun lot éligible (retenue uniquement sur les clients qui stockent)'}
                  </p>
                </div>
                <div className="bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-[0.08em] text-slate-500">Huile à verser en cuve (retenue incluse)</p>
                  <p data-testid="a-verser-kg" className="tabular font-data text-lg font-semibold text-slate-900">{fmt(aVerser, 3)} Kg</p>
                  <p className={`text-[10px] ${Math.abs(reste) > 0.05 ? 'font-bold text-red-700' : 'text-slate-500'}`}>
                    Reste à répartir : {fmt(reste, 3)} Kg (tolérance ± 0,05 Kg)
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {TOUCHES.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`pave-${t}`}
                  onClick={() => taper(t)}
                  className="flex h-16 items-center justify-center border-2 border-slate-300 bg-white font-data text-2xl font-semibold text-slate-900 transition-colors duration-150 active:bg-slate-900 active:text-white hover:bg-slate-100"
                >
                  {t === 'del' ? <Delete size={22} /> : t}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4 bg-white px-5 py-5">
            {clientLot && (
              <div data-testid="emballages-atelier" className="border-2 border-slate-900">
                <p className="border-b border-slate-200 bg-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-white">
                  Emballages fournis au client (facultatif)
                </p>
                <div className="divide-y divide-slate-100">
                  {formats.map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{f.nom}</p>
                        <p className="font-data text-[11px] text-slate-500">
                          stock vides : {fmt(f.stock)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid={`emb-minus-${f.id}`}
                          onClick={() => stepEmb(f.id, -1)}
                          className="h-11 w-11 border-2 border-slate-300 text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          −
                        </button>
                        <input
                          data-testid={`emb-qte-${f.id}`}
                          type="number"
                          min="0"
                          value={emb[f.id] ?? 0}
                          onChange={(e) => setEmb((v) => ({ ...v, [f.id]: Math.max(0, Number(e.target.value)) }))}
                          className="tabular h-11 w-16 border-2 border-slate-300 bg-slate-50 px-1 text-center font-data text-base outline-none focus:border-slate-900 focus:bg-white"
                        />
                        <button
                          type="button"
                          data-testid={`emb-plus-${f.id}`}
                          onClick={() => stepEmb(f.id, 1)}
                          className="h-11 w-11 border-2 border-slate-300 text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="border-t border-slate-200 bg-slate-50 px-3 py-2 font-data text-[11px] text-slate-600">
                  {lignesEmb.length
                    ? `${lignesEmb.reduce((s, l) => s + l.qte, 0)} contenant(s) déclaré(s) — le montant sera facturé au client par l’administration.`
                    : 'Aucun emballage déclaré — le client repart avec ses propres contenants, rien ne lui est facturé.'}
                </p>
              </div>
            )}

            {devenirOuvert && (
              <div data-testid="choix-devenir" className="grid grid-cols-2 gap-px bg-slate-200">
                {[['cuve', 'Verser en cuve (dépôt client)'], ['sortie', 'Sortie immédiate (le client emporte)']].map(([id, lib]) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`choix-devenir-${id}`}
                    onClick={() => setChoixDevenir(id)}
                    className={`px-3 py-3 text-left text-sm font-bold transition-colors duration-150 ${choixDevenir === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    {lib}
                  </button>
                ))}
              </div>
            )}

            {sortieDirecte ? (
              <p data-testid="sortie-directe-info" className="border-2 border-slate-900 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-800">
                SORTIE IMMÉDIATE — l'huile part avec le client. Aucune cuve à choisir : le volume est
                affecté automatiquement à la sortie directe.
              </p>
            ) : (
            <div data-testid="repartition-cuves">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                  Répartition sur les cuves
                </p>
                <button
                  type="button"
                  data-testid="ajouter-cuve-sortie"
                  onClick={ajouterLigne}
                  className="flex h-9 items-center gap-1.5 border-2 border-slate-900 px-3 text-xs font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                >
                  <Plus size={14} /> Ajouter une cuve
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {lignes.map((l, i) => {
                  const c = cuves.find((x) => x.id === l.cuveId);
                  return (
                    <div key={i} data-testid={`sortie-ligne-${i}`} className="flex flex-col gap-2 border border-slate-200 p-2 sm:flex-row sm:items-center">
                      <select
                        data-testid={`sortie-select-cuve-${i}`}
                        value={l.cuveId}
                        onChange={(e) => majLigne(i, 'cuveId', e.target.value)}
                        className="h-12 flex-1 border-2 border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white"
                      >
                        <option value="">— Choisir la cuve —</option>
                        {cuvesTriees.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.nom} · {x.famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'} · reste {fmt(x.capacite - x.volume)} {x.unite}
                          </option>
                        ))}
                      </select>
                      <input
                        data-testid={`sortie-kg-${i}`}
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="Kg"
                        value={l.kg}
                        onChange={(e) => majLigne(i, 'kg', e.target.value)}
                        className="h-12 w-full border-2 border-slate-300 bg-slate-50 px-2 text-center font-data text-sm outline-none focus:border-slate-900 focus:bg-white sm:w-28"
                      />
                      <button
                        type="button"
                        data-testid={`sortie-remplir-${i}`}
                        onClick={() => majLigne(i, 'kg', String(Math.max(0, (Number(l.kg) || 0) + reste)))}
                        className="h-12 border border-slate-300 px-2 font-data text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        Solde
                      </button>
                      <button
                        type="button"
                        data-testid={`sortie-retirer-${i}`}
                        onClick={() => retirerLigne(i)}
                        className="h-12 border border-slate-300 px-2 text-slate-600 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 size={14} />
                      </button>
                      {c && (
                        <span className="font-data text-[11px] text-slate-500 sm:w-32">
                          après : {fmt(c.volume + (Number(l.kg) || 0))} {c.unite}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {!sortieDirecte && croisee && (
              <p data-testid="sortie-blocage" className="flex items-start gap-2 border-2 border-red-600 bg-red-50 px-3 py-3 text-sm font-semibold text-red-800">
                <ShieldAlert size={18} className="mt-0.5 flex-shrink-0" />
                <span>
                  BLOCAGE SÉCURITÉ — un lot {famille === 'lampante' ? 'LAMPANTE' : 'NOBLE'} ne peut pas
                  être versé dans la cuve « {croisee.nom} » ({croisee.famille === 'lampante' ? 'lampante' : 'noble'}).
                </span>
              </p>
            )}

            <div className="border border-slate-200">
              <p className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Lots déclarés
              </p>
              <ul className="divide-y divide-slate-100">
                {lotsActifs.map((l) => (
                  <li key={l.id} className="flex justify-between px-3 py-2 text-sm">
                    <span className="text-slate-700">
                      {l.id} · {nomTiers(l.tiersId)}
                    </span>
                    <span className="font-data text-slate-900">{fmt(decompte(l).poidsNet)} Kg</span>
                  </li>
                ))}
              </ul>
            </div>

            {erreur && (
              <p data-testid="sortie-erreur" className="border-2 border-red-600 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
                {erreur}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3 border-t-2 border-slate-900 px-5 py-4">
          <button
            type="button"
            data-testid="sortie-valider"
            disabled={bloque}
            onClick={() => valider(false)}
            className={`flex h-16 flex-1 items-center justify-center gap-3 font-heading text-lg font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 ${
              bloque ? 'cursor-not-allowed bg-slate-300' : 'bg-emerald-700 hover:bg-emerald-600'
            }`}
          >
            <Check size={22} /> {sortieDirecte ? 'Valider et remettre au client' : 'Valider et verser en cuve'}
          </button>
          <button
            type="button"
            data-testid="sortie-annuler"
            onClick={onClose}
            className="h-16 border-2 border-slate-300 px-6 font-heading text-base font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};
